// Day-of-week intensity pattern. Used to flag whether a forecast date lands
// on a barber's typical peak day, slow day, or an average one — drives
// playbook selection in weatherTipKind() so the same weather can produce a
// different tip on a busy Saturday vs. a quiet Tuesday.
//
// Method:
//   1. Take the last N days of visits (default 90 → last ~13 weeks of activity).
//   2. Bucket by day-of-week (0=Sun … 6=Sat). Each bucket gets a count of cuts.
//   3. Compute the median across the 7 buckets.
//   4. A day is 'peak'    when its count is >= 1.3 × median (or absolute floor).
//      A day is 'slow'    when its count is <= 0.6 × median (or absolute floor).
//      A day is 'average' otherwise.
//
// Why ratios + a floor: a barber with 5 cuts on Sat and 0 on Mon shouldn't
// have Mon flagged "slow" (it's just empty). We require both a ratio gap
// AND a baseline volume so the flags only fire once a real pattern exists.
//
// Cold-start default: under MIN_VISITS_FOR_PATTERN we don't have enough
// signal yet, so we fall back to the barber-industry default — Sat/Sun
// peak, Tue/Wed/Thu slow, Mon/Fri average. Once the user has 2+ months
// of bookings the data-driven pattern takes over and the seed is replaced.

import type { VisitRow } from '@busy/api';

export type DayOfWeekIntensity = 'peak' | 'slow' | 'average';

/** 0=Sun, 1=Mon, … 6=Sat. */
export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const PEAK_RATIO = 1.3;
const SLOW_RATIO = 0.6;
const MIN_VISITS_FOR_PATTERN = 14;
/** Each tier needs at least this many cuts in absolute terms before being
 *  classified. Otherwise a barber with 1 cut/wk gets noisy peak/slow flags. */
const MIN_BUCKET_FLOOR = 3;

/** Cold-start fallback used until the barber has enough booking history.
 *  Barber-industry baseline: weekends peak, midweek slow, Mon/Fri average.
 *  Replaced by data-driven classification once 2+ months of cuts exist. */
const INDUSTRY_DEFAULT: Record<DayOfWeek, DayOfWeekIntensity> = {
  0: 'peak',    // Sun
  1: 'average', // Mon
  2: 'slow',    // Tue
  3: 'slow',    // Wed
  4: 'slow',    // Thu
  5: 'average', // Fri
  6: 'peak',    // Sat
};

export function dayOfWeekPattern(
  visits: VisitRow[] | null | undefined,
): Record<DayOfWeek, DayOfWeekIntensity> {
  const counts: Record<DayOfWeek, number> = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
  for (const v of visits ?? []) {
    const d = new Date(v.started_at);
    if (Number.isNaN(d.getTime())) continue;
    const dow = d.getDay() as DayOfWeek;
    counts[dow] += 1;
  }
  const total = (Object.values(counts) as number[]).reduce((a, b) => a + b, 0);

  // Cold-start: use the industry baseline until enough cuts accumulate to
  // trust the user's actual pattern.
  if (total < MIN_VISITS_FOR_PATTERN) {
    return { ...INDUSTRY_DEFAULT };
  }

  const sorted = [...Object.values(counts)].sort((a, b) => a - b);
  const median = sorted[3] ?? 0; // middle of 7 sorted values

  const out: Record<DayOfWeek, DayOfWeekIntensity> = {
    0: 'average', 1: 'average', 2: 'average', 3: 'average', 4: 'average', 5: 'average', 6: 'average',
  };
  for (const k of [0, 1, 2, 3, 4, 5, 6] as DayOfWeek[]) {
    const c = counts[k];
    if (c >= MIN_BUCKET_FLOOR && c >= median * PEAK_RATIO) out[k] = 'peak';
    else if (c <= median * SLOW_RATIO && median >= MIN_BUCKET_FLOOR) out[k] = 'slow';
  }
  return out;
}

/** Day-of-week (0..6) for a YYYY-MM-DD date string parsed in local time.
 *  Matches the way ForecastDay.date is interpreted in WeatherTile. */
export function dowFromIsoDate(iso: string): DayOfWeek {
  return new Date(iso + 'T00:00:00').getDay() as DayOfWeek;
}
